import { useCallback, useState } from 'react';
import { getLocalStorage, getSessionStorage } from '@shared/core/storage';
import type { Storage } from '@shared/core/storage';

type UsePersistedStateOptions<T> = Readonly<{
  /** Storage backend. Defaults to localStorage. */
  storage?: Storage;
  /** Custom serializer. Defaults to identity for strings, JSON.stringify otherwise. */
  serialize?: (value: T) => string;
  /** Custom deserializer. Return `undefined` to fall back to `defaultValue`. Defaults to identity for strings, JSON.parse otherwise. */
  deserialize?: (raw: string) => T | undefined;
}>;

function usePersistedState<T>(key: string, defaultValue: T, options?: UsePersistedStateOptions<T>): readonly [T, (value: T) => void] {
  const { storage: storageBackend, serialize, deserialize } = options ?? {};
  const getStorage = () => storageBackend ?? getLocalStorage();

  const [state, setState] = useState<T>(() => {
    try {
      const raw = getStorage().getItem(key);
      if (raw == null) return defaultValue;
      if (deserialize) return deserialize(raw) ?? defaultValue;
      try {
        return JSON.parse(raw) as T;
      } catch {
        return raw as unknown as T;
      }
    } catch {
      return defaultValue;
    }
  });

  const setPersistedState = useCallback(
    (value: T) => {
      try {
        const serialized = serialize ? serialize(value) : typeof value === 'string' ? value : JSON.stringify(value);
        getStorage().setItem(key, serialized);
      } catch {
        // best-effort persistence
      }
      setState(value);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key, storageBackend, serialize],
  );

  return [state, setPersistedState] as const;
}

export default usePersistedState;

function useSessionPersistedState<T>(key: string, defaultValue: T, options?: Omit<UsePersistedStateOptions<T>, 'storage'>): readonly [T, (value: T) => void] {
  return usePersistedState(key, defaultValue, { ...options, storage: getSessionStorage() });
}

export { useSessionPersistedState };
